import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Plus, Lock, History } from "lucide-react";
import { PHYSIO_STATUS } from "@/lib/physio";

export default function PhysioSlotCard({ slot, onReserve, onBlock, onAction, onHistory }) {
  const st = PHYSIO_STATUS[slot.status] || PHYSIO_STATUS.available;
  const Icon = st.icon;
  const appt = slot.appointment;
  const isAvailable = slot.status === "available";
  const isBlocked = slot.status === "blocked";

  return (
    <div className={`rounded-xl border p-3 flex flex-col gap-2 ${isAvailable ? "border-dashed border-slate-300 bg-slate-50" : "border-slate-200 bg-white"}`}
      data-testid={`physio-slot-${slot.start}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-black tabular-nums" style={{ fontFamily: "Nunito" }}>{slot.start}–{slot.end}</span>
        <Badge variant="outline" className={`gap-1 ${st.badge}`} data-testid={`physio-slot-status-${slot.start}`}>
          <Icon className="h-3 w-3" /> {st.label}
        </Badge>
      </div>

      {appt && (
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate" data-testid={`physio-slot-player-${slot.start}`}>
            {appt.player_name}{appt.dorsal ? ` · #${appt.dorsal}` : ""}
          </div>
          <div className="text-xs text-slate-500 truncate">{appt.team_name}{appt.category ? ` · ${appt.category}` : ""}</div>
          {appt.responsible_person && <div className="text-[11px] text-slate-400 truncate">Resp.: {appt.responsible_person}</div>}
          {appt.reason && <div className="text-[11px] text-slate-400 truncate">Motivo: {appt.reason}</div>}
        </div>
      )}
      {isBlocked && <div className="text-xs text-slate-500">{slot.block?.reason || "Bloqueado"}</div>}

      <div className="flex items-center gap-1 mt-auto">
        {isAvailable && (
          <>
            <Button size="sm" className="flex-1 bg-purple-700 hover:bg-purple-800 h-8" onClick={onReserve}
              data-testid={`physio-slot-reserve-${slot.start}`}>
              <Plus className="h-4 w-4 mr-1" /> Reservar
            </Button>
            <Button size="sm" variant="outline" className="h-8 px-2" onClick={onBlock}
              title="Bloquear" data-testid={`physio-slot-block-${slot.start}`}>
              <Lock className="h-4 w-4" />
            </Button>
          </>
        )}
        {isBlocked && (
          <Button size="sm" variant="outline" className="flex-1 h-8" onClick={() => onAction("unblock", slot.block)}
            data-testid={`physio-slot-unblock-${slot.start}`}>
            Desbloquear
          </Button>
        )}
        {appt && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="flex-1 h-8" data-testid={`physio-slot-actions-${slot.start}`}>
                <MoreVertical className="h-4 w-4 mr-1" /> Acciones
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onAction("edit", appt)} data-testid={`physio-action-edit-${slot.start}`}>Editar / Reprogramar</DropdownMenuItem>
              {appt.status === "booked" && <DropdownMenuItem onClick={() => onAction("confirmed", appt)} data-testid={`physio-action-confirm-${slot.start}`}>Confirmar</DropdownMenuItem>}
              {(appt.status === "booked" || appt.status === "confirmed") && (
                <>
                  <DropdownMenuItem onClick={() => onAction("completed", appt)} data-testid={`physio-action-complete-${slot.start}`}>Marcar completada</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onAction("no_show", appt)} data-testid={`physio-action-noshow-${slot.start}`}>Marcar no presentado</DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem onClick={() => onHistory(appt.player_id)} data-testid={`physio-action-history-${slot.start}`}>
                <History className="h-4 w-4 mr-2" /> Historial del jugador
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-rose-600" onClick={() => onAction("cancelled", appt)} data-testid={`physio-action-cancel-${slot.start}`}>Cancelar cita</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
